import {SlidersHorizontal} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import {FilterTriggerStyle} from 'src/components/FilterDropdown';
import {ResetFiltersButton} from 'src/components/ResetFiltersButton';
import Layer from 'src/Layer/Layer';
import {cn} from 'src/lib/utils';
import {Modal} from 'src/Modal';
import {useIsMobile} from 'src/useIsMobile';

export type FilterPillRowItem = {
  /** Stable identity so a pill keeps its component state as the row re-sorts. */
  key: string;
  /** Whether the filter currently holds a value; active pills float forward. */
  active: boolean;
  pill: React.ReactNode;
};

type Props = {
  pills: FilterPillRowItem[];
  /** Clears every filter; pass it only while there is something to clear. */
  onReset?: () => void;
};

/**
 * The filter row the list headers share.
 *
 * On a wide screen every filter is a pill on one non-wrapping row that
 * scrolls sideways if it overflows. Pills holding a value float to the front
 * (keeping their relative order), and Reset sits outside the scroller, so
 * neither an active filter nor the way to clear it can end up off the edge.
 *
 * On a phone a scrolling row hides most of its own options, so it becomes
 * one Filters button — its count says the list is narrowed — followed by the
 * active filters as pills. The button opens a sheet with every filter as a
 * full-width row: the same filter components and menus, drawn as rows.
 */
export const FilterPillRow = ({pills, onReset}: Props) => {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const activeCount = pills.filter((item) => item.active).length;
  const reset = onReset ?? null;

  if (isMobile) {
    return (
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className={cn(
            'border-border flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-sm whitespace-nowrap',
            activeCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground',
          )}
        >
          <SlidersHorizontal className="size-3.5" />
          {activeCount > 0 ? `Filters · ${activeCount}` : 'Filters'}
        </button>
        <div className="no-scrollbar flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
          {pills
            .filter((item) => item.active)
            .map((item) => (
              <React.Fragment key={item.key}>{item.pill}</React.Fragment>
            ))}
        </div>
        <Layer>
          {sheetOpen && (
            <Modal
              header="Filters"
              onRequestClose={() => setSheetOpen(false)}
              // A sheet as tall as its rows, not the full-height page a
              // phone modal usually is.
              className="top-auto bottom-0 h-auto max-h-[85dvh] rounded-b-none"
              footer={
                <>
                  {reset != null && (
                    <Button variant="ghost" className="mr-auto" onClick={reset}>
                      Reset
                    </Button>
                  )}
                  <Button variant="primary" onClick={() => setSheetOpen(false)}>
                    Done
                  </Button>
                </>
              }
            >
              <FilterTriggerStyle.Provider value="row">
                <div className="-my-4 flex flex-col [&>*:last-child]:border-b-0">
                  {pills.map((item) => (
                    <React.Fragment key={item.key}>{item.pill}</React.Fragment>
                  ))}
                </div>
              </FilterTriggerStyle.Provider>
            </Modal>
          )}
        </Layer>
      </div>
    );
  }

  const sorted = [...pills].sort(
    (a, b) => Number(b.active) - Number(a.active),
  );
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="no-scrollbar flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
        {sorted.map((item) => (
          <React.Fragment key={item.key}>{item.pill}</React.Fragment>
        ))}
      </div>
      {reset != null && <ResetFiltersButton onReset={reset} />}
    </div>
  );
};
